import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-stepper>` says about itself.
 *
 * IT IS THE RAIL, NOT THE FLOW. The wizard blueprint is a pinned rail beside the active step's
 * content; this draws the rail. It does not own the step's content, decide what "next" means, or
 * move between steps — the surrounding page does that and tells this piece where the person now is
 * by setting one value. A piece that owned the sequencing would have to know what the flow is for,
 * which is the opposite of a catalogue component.
 *
 * WHY THE STATES ARE NOT THIS PIECE'S WORDS. A completed step wears the platform success mark and
 * a failed one the danger mark; the step being worked on carries the accent and the selected tint
 * the design system already defines. None of that is a stepper idea of "done" — it is the existing
 * display vocabulary, which is what stops a catalogue growing a private notion of state per piece.
 *
 * DECLARED STATE BEATS DERIVED STATE. Position implies three of the four: before the current step
 * is completed, at it is current, after it is still to come. `error` is the one no position can
 * imply, which is precisely why a step may declare its own state and why that declaration wins.
 */
const docs: ComponentDocs = {
  aliases: ['stepper', 'progress indicator', 'wizard rail', 'step rail', 'guided flow', 'step indicator', 'path'],

  answers: [
    'show the steps of a guided flow and which one somebody is on',
    'show which steps are already done',
    'show that a step failed rather than merely being unfinished',
    'let somebody go back to a step they have already completed',
    'drive the rail of a wizard surface',
  ],

  // A step of a guided flow. The wizard blueprint's `rail` region is the place this is for.
  placements: ['screen'],

  inputs: [
    {
      name: 'steps',
      // No attribute: a list cannot ride one. Set as a property, exactly as the tab strip's tabs are.
      type: 'json',
      default: [],
      description:
        'The steps, in order, as `{ key, label, description?, state? }` objects or as plain strings. A ' +
        'string is shorthand for a step whose key and label are the same word. `key` is what `current` ' +
        'names and what a selection reports; `description` is one quieter line under the label. An empty ' +
        'list draws nothing rather than an empty rail.',
    },
    {
      name: 'current',
      attribute: 'current',
      property: true,
      type: 'text',
      description:
        'The key of the step being worked on. Every earlier step reads as completed and every later one ' +
        'as still to come, so moving a flow forward is one assignment rather than a rewritten list. A key ' +
        'that matches no step leaves every step reading as still to come, which is the honest drawing of ' +
        '"this flow has not started".',
    },
    {
      name: 'orientation',
      attribute: 'orientation',
      type: 'text',
      options: ['vertical', 'horizontal'],
      // OPEN, not closed: the piece tests for `horizontal` and draws the vertical rail for anything
      // else. Declaring a closed set here would have the platform refuse a value this draws fine.
      fallback: 'vertical',
      description:
        'Which way the steps run. `vertical` is the rail the wizard blueprint pins beside the content; ' +
        '`horizontal` is the band across the top of a surface that has no rail. Anything unrecognised ' +
        'reads as `vertical`.',
      default: 'vertical',
    },
    {
      name: 'interactive',
      attribute: 'interactive',
      type: 'boolean',
      description:
        'Let somebody return to a step they have already finished. Only a completed or failed step ' +
        'becomes a control; the step in hand and the ones still ahead never do, because a rail that ' +
        'offers a step the flow cannot yet answer is offering something that does not work. Without ' +
        'this the rail is a report and nothing in it is clickable.',
      default: false,
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'What the sequence is called, used as the accessible name of the list. Unset leaves the list ' +
        'unnamed, which is what a rail with a visible heading beside it should do rather than saying the ' +
        'same thing twice.',
    },
  ],

  events: [
    {
      name: 'caos-step-select',
      when: 'A completed or failed step is chosen, and only while `interactive` is set. The rail does not move itself; the page decides what going back means.',
      carries: '`{ key, index }` — which step was chosen, and where it sits in the list.',
    },
  ],

  // Nothing is readable that a page cannot also set: `current` is an input, and the state of a step
  // is derived rather than held. `stateOf` answers for one step and is a method, below.
  state: [],

  // The steps are data, not content. There is no slot and nothing goes inside it.
  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of a step label and its supporting line.' },
    { token: '--caos-font-mono', controls: 'The typeface of the step number inside the marker.' },
    { token: '--caos-color-text', controls: 'The label of the step in hand and of a completed one.' },
    { token: '--caos-color-text-muted', controls: 'A step still to come, and every supporting line.' },
    { token: '--caos-color-border', controls: 'The marker outline of a step still to come, and the line joining the steps.' },
    { token: '--caos-color-surface', controls: 'The fill inside a marker.' },
    { token: '--caos-color-accent', controls: 'The marker of the step being worked on.' },
    { token: '--caos-color-success', controls: 'The mark and outline of a completed step, and the line behind it.' },
    { token: '--caos-color-danger', controls: 'The mark, outline and label of a failed step.' },
    { token: '--caos-state-selected-bg', controls: 'The tint behind the step being worked on.' },
    { token: '--caos-state-hover-bg', controls: 'The tint behind a reachable step under the pointer.' },
    { token: '--caos-state-disabled-opacity', controls: 'The dimming of a step still ahead, while the rail is offering a way back to an earlier one.' },
    { token: '--caos-focus-ring', controls: 'The ring on a reachable step reached by keyboard.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of a step row.' },
    { token: '--caos-radius-pill', controls: 'The roundness of the marker.' },
    { token: '--caos-space-2', controls: 'The padding inside a step row, and the gap beside the marker.' },
    { token: '--caos-space-3', controls: 'The gap between the marker and the label, and the length of the joining line.' },
  ],

  parts: [
    { part: 'list', is: 'the whole sequence' },
    { part: 'step', is: 'one step — a button where it can be returned to, and otherwise not a control' },
    { part: 'marker', is: 'the numbered disc, or the success or danger mark that replaces the number' },
    { part: 'label', is: 'the name of a step' },
    {
      part: 'description',
      is: 'the quieter line under a label',
      when: 'Only on a step that declares a description. A step without one carries nothing under this name.',
    },
    {
      part: 'connector',
      is: 'the line joining one step to the next, drawn in the success colour once the step above it is complete',
      when: 'Between steps only. The last step has nothing after it, so it carries no connector.',
    },
  ],

  methods: [
    {
      name: 'stateOf',
      description:
        'The state one step is drawn in — what it declared, or what its position implies. Exposed because ' +
        'it is the whole judgement this piece makes, and anything that re-derives it is reading its own ' +
        'copy of the rule rather than the one that draws.',
      args: [{ name: 'index', type: 'number', description: 'Which step, counting from zero.' }],
    },
  ],

  placing:
    'In the `rail` region of a page built on the wizard blueprint, with the step content in `main`. The ' +
    'page sets `current` as the flow moves and hands back a step declaring `error` when one fails. It can ' +
    'also go across the top of any surface that has no rail, by setting `orientation` to `horizontal`.',

  whenToUse:
    'When there is a sequence somebody is partway through and the order matters. If the parts can be done ' +
    'in any order, they are not steps and a rail claiming otherwise is wrong about the thing it draws.',

  insteadUse: [
    { name: 'ProgressBar', when: 'The progress is a quantity rather than a set of named stages.' },
    { name: 'TabStrip', when: 'The parts are facets of one thing that can be visited in any order.' },
    { name: 'Breadcrumb', when: 'You are showing where a page sits in a hierarchy, not progress through a flow.' },
    { name: 'Tree', when: 'The sequence is really a nested structure somebody navigates.' },
  ],

  accessibility:
    'An ordered list of list items, so the count and the order are announced rather than implied by ' +
    'drawing. The step in hand carries `aria-current="step"`. Every marker is hidden from assistive ' +
    'technology: the number repeats the list position and the success or danger mark repeats what the ' +
    'label and `aria-current` already say, so announcing them again is noise. A step that can be returned ' +
    'to is a real `button` and reachable by keyboard; one that cannot is not dressed as a control, which ' +
    'is the difference between a rail that reports and one that lies about what it offers. Name the ' +
    'sequence with `label` unless a visible heading beside it already does.',

  examples: [
    {
      key: 'the-rail',
      label: 'The wizard rail',
      summary:
        'Four steps with the third in hand: the two before it are complete, the one after is still to come. ' +
        'This is the shape the wizard blueprint pins beside the step content.',
      placement: {
        component: 'caos-stepper',
        inputs: {
          label: 'Set up your org',
          current: 'people',
          steps: [
            { key: 'org', label: 'Organisation', description: 'Name it and pick a region.' },
            { key: 'objects', label: 'Objects', description: 'The records you keep.' },
            { key: 'people', label: 'People', description: 'Who gets in, and what they may see.' },
            { key: 'review', label: 'Review', description: 'Check it over before it goes live.' },
          ],
        },
      },
    },
    {
      key: 'a-step-failed',
      label: 'A step that failed',
      summary:
        'The one state position cannot imply. The second step declares `error`, so it keeps that state ' +
        'while the flow has moved on past it — and with `interactive` set it is one of the two kinds of ' +
        'step somebody may go back to.',
      placement: {
        component: 'caos-stepper',
        inputs: {
          label: 'Import your data',
          current: 'map',
          interactive: true,
          steps: [
            { key: 'upload', label: 'Upload' },
            { key: 'validate', label: 'Validate', description: 'Nine rows have no account.', state: 'error' },
            { key: 'map', label: 'Map columns' },
            { key: 'import', label: 'Import' },
          ],
        },
      },
    },
    {
      key: 'across-the-top',
      label: 'Across the top',
      summary: 'The same sequence running horizontally, for a surface with no rail to pin it beside.',
      placement: {
        component: 'caos-stepper',
        inputs: {
          orientation: 'horizontal',
          current: 'pay',
          steps: ['Basket', 'Address', 'Pay', 'Done'],
        },
      },
    },
  ],
};

export default docs;
